import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnInit } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { filter } from 'rxjs';
import { MessageDialogComponent, MessageBoxCustomButton } from 'src/app/angular-core/components/message-dialog/message-dialog.component';
import { IconStatus } from 'src/app/angular-core/interfaces/list-item.interfaces';
import { LocalizedDatePipe } from 'src/app/angular-core/utils/pipes/localized-date/localized-date.pipe';
import { BasicXpacInfo } from 'src/app/generated/CrossPoint.RemoteXpac.Server';
import { DevicesActionsTypes, getRestoreDevicesAction, refreshDevicesAction, setRestoreDevicesAction } from 'src/app/store/actions/devices.actions';
import { selectUserData } from 'src/app/store/selectors/auth.selectors';
import { selectRestoreDevices } from 'src/app/store/selectors/devices.selectors';
import { Roles } from 'src/app/utils/enums/roles';
import { hasValue } from 'src/app/angular-core/utils/functions/common.functions';
import { hasRoleUserData } from 'src/app/utils/functions/permission.function';
import { getXpacInfoStringAddressAndRetailer, getXpacInfoStringIdAndInstallationDate } from 'src/app/utils/functions/xpac.functions';
import { IUserData } from 'src/app/utils/interfaces/auth.interfaces';
import { IRemoteXPACPortalState, IRestoreDevicesXpac } from 'src/app/utils/interfaces/store.interfaces';
import { IGetRestoreDevicesData } from 'src/app/utils/interfaces/remote-maintenance.interfaces';

@UntilDestroy()
@Component({
  selector: 'xpac-xpac-replacement',
  templateUrl: './xpac-replacement.component.html',
  styleUrls: ['./xpac-replacement.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class XpacReplacementComponent implements OnInit {
  @Input() public xpac: BasicXpacInfo = null;

  public isLoading: boolean = true;  
  public isLoadingRestoreDevices: boolean = false;
  public devicesToRestore: BasicXpacInfo[] = [];
  public searchText: string = '';
  public replaced: boolean = false;

  private userData: IUserData = null;
  private isReplacing: boolean = false;
  private dialogRef: MatDialogRef<any> = null;

  constructor(
    private store: Store<IRemoteXPACPortalState>,
    private cdr: ChangeDetectorRef,
    private translate: TranslateService,
    private datePipe: LocalizedDatePipe,
    private actionsListener$: Actions,
    public dialog: MatDialog,
  ) { }

  public ngOnInit(): void {
    this.initListeners(); 
    let data: IGetRestoreDevicesData = { search: this.searchText, deviceId: this.xpac.deviceId };
    this.isLoadingRestoreDevices = true;
    this.store.dispatch(getRestoreDevicesAction({ data: data }));    
    this.isLoading = false;
    this.cdr.markForCheck();
  }

  public async onSearchTextChanged(searchText: string): Promise<void> {
    this.searchText = searchText;
    this.devicesToRestore = [];    

    while (this.isLoadingRestoreDevices) {
      // Wait a little so an previous call can be dispatched first.
      await new Promise(resolve => setTimeout(resolve, 100));
    }

    let data: IGetRestoreDevicesData = { 
      deviceId: this.xpac.deviceId, 
      search: this.searchText 
    };

    this.isLoadingRestoreDevices = true;
    this.cdr.markForCheck();
    this.store.dispatch(getRestoreDevicesAction({ data: data }));
  }

  public replace(xpac: BasicXpacInfo): void{
    let headerText: string = this.translate.instant('Modules.XpacReplacementComponent.ReplacementTitle');
    let messageText: string = this.translate.instant('Modules.XpacReplacementComponent.ReplacementInfo');

    if(this.xpac.hasSubscription){
      messageText = this.translate.instant('Modules.XpacReplacementComponent.ReplacementWithLicenseInfo');    
    }

    const dialogRef: MatDialogRef<MessageDialogComponent> = this.dialog.open(MessageDialogComponent, {
      panelClass: 'messagebox-dialog',
      autoFocus: false,
      data: { headerInfo: headerText, message: messageText, buttons: MessageBoxCustomButton.YesNo }
    });

    dialogRef.afterClosed().subscribe((result: boolean) => {
      if (result) {
        if (this.dialogRef) {
          this.dialogRef.close();
          this.dialogRef = null;
        }

        this.dialogRef = this.dialog.open(MessageDialogComponent, {
          panelClass: 'messagebox-dialog',
          autoFocus: false,
          data: { 
            headerInfo: headerText,
            message: this.translate.instant('Modules.XpacReplacementComponent.ReplacementBusy'),
            buttons: MessageBoxCustomButton.None,
            disableClose: true,
          }
        });
        this.isReplacing = true;

        this.store.dispatch(setRestoreDevicesAction({
          data: {serialTarget: this.xpac.deviceId, serialOld: xpac.deviceId}
        }));
      }
    });
  }

  public getActionIcon(): IconStatus {
    return {icon: "swap_horiz"};
  }

  public getTitleString(xpac: BasicXpacInfo): string {
    let title: string = "";

    if(xpac.storeName !== ""){
      title = xpac.storeName;
    }
    else if(xpac.deviceId !== ""){
      title = xpac.deviceId;
    }
    else{
      // default no name
      title = this.translate.instant("Modules.RemoteMaintenance.NoName");
    }

    return title;
  }

  public getXpacInfoStringIdAndInstallationDate(xpac: BasicXpacInfo): string {
    return getXpacInfoStringIdAndInstallationDate(xpac, this.datePipe);
  }

  public getXpacInfoStringAddressAndRetailer(xpac: BasicXpacInfo): string {
    return getXpacInfoStringAddressAndRetailer(xpac, hasRoleUserData(Roles.Crosspoint, this.userData));
  }
  
  private initListeners(): void {
    this.store.select(selectUserData)
      .pipe(
        filter((value: IUserData | null) => value !== null),
        untilDestroyed(this)
      )
      .subscribe((userData: IUserData) => {
        this.userData = userData;
        this.cdr.markForCheck();
      });
    
    this.store.select(selectRestoreDevices(this.xpac.deviceId))
    .pipe(
      filter(hasValue),
      untilDestroyed(this)
    )
    .subscribe((xpacsRestoreDevices: IRestoreDevicesXpac) => {      
        this.devicesToRestore = xpacsRestoreDevices.devices;
        this.isLoading = false;
        this.isLoadingRestoreDevices = false;
        this.cdr.markForCheck();      
    });

    this.actionsListener$
      .pipe(ofType(DevicesActionsTypes.SET_RESTORE_DEVICE_FAILED))
      .pipe(untilDestroyed(this))
      .subscribe(() => {
        // Make sure the dialog is closed, regular error dialog should pop up as well
        if (this.dialogRef) {
          this.dialogRef.close();
          this.dialogRef = null;
        }
      });

    this.actionsListener$
      .pipe(ofType(DevicesActionsTypes.SET_RESTORE_DEVICE_SUCCESS))
      .pipe(untilDestroyed(this))
      .subscribe(() => {
        if (this.isReplacing && !this.replaced) {
          if (this.dialogRef) {
            this.dialogRef.close();
            this.dialogRef = null;
          }

          this.replaced = true;
          this.cdr.markForCheck();

          // We will be returned to the XPAC list that still contains the old XPAC names. To prevent issues we issue a refresh here.
          this.store.dispatch(refreshDevicesAction());

          const headerText: string = this.translate.instant('Modules.XpacReplacementComponent.ReplacementSuccess');
          const messageText: string = this.translate.instant('Modules.XpacReplacementComponent.ReplacementSuccessMessage')
          this.dialog.open(MessageDialogComponent, {
            panelClass: 'messagebox-dialog',
            autoFocus: false,
            data: { headerInfo: headerText, message: messageText, buttons: MessageBoxCustomButton.OK }
          }).afterClosed().subscribe(() => {
            // Ensure the XPAC info dialog we are currently in is closed.
            this.dialog.closeAll();
          });
        }
      });
  }
}